import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLoginModal } from "@/hooks/use-login-modal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { Eye, EyeOff } from "lucide-react";

type UserSafe = {
  id: string;
  name?: string | null;
  email: string;
  verified?: boolean;
  idUploaded?: boolean;
  phone?: string | null;
};

export function LoginModal() {
  const navigate = useNavigate();
  const { isOpen, closeLoginModal } = useLoginModal();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);

  async function handleResendVerification() {
    if (!email) {
      return toast({
        title: "MISSING EMAIL",
        description: "PLEASE ENTER YOUR EMAIL ADDRESS",
      });
    }

    setResendingVerification(true);
    try {
      const res = await fetch("/api/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase() }),
      });

      let text = "";
      if (!res.bodyUsed) {
        try {
          text = await res.text();
        } catch (e) {
          text = "";
        }
      }

      let data: any = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        data = null;
      }

      if (!res.ok) {
        throw new Error(
          data?.error || text || "Failed to resend verification email",
        );
      }

      toast({
        title: "EMAIL SENT",
        description:
          "Verification email has been sent to " +
          email +
          ". Please check your inbox and spam folder.",
      });
    } catch (err: any) {
      console.error("[LoginModal] Resend verification error:", err);
      toast({
        title: "ERROR",
        description: err?.message || "Failed to resend verification email",
      });
    } finally {
      setResendingVerification(false);
    }
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();

    if (!email || !password) {
      return toast({
        title: "MISSING FIELDS",
        description: "PLEASE PROVIDE EMAIL AND PASSWORD",
      });
    }

    setLoading(true);
    setEmailNotConfirmed(false);
    try {
      console.log("[LoginModal] Attempting login for:", email);
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',  // 🔐 Allow cookies to be sent and stored
        body: JSON.stringify({ email: email.toLowerCase(), password }),
      });

      console.log("[LoginModal] Login response status:", res.status);

      let text = "";
      if (!res.bodyUsed) {
        try {
          text = await res.text();
        } catch (e) {
          text = "";
        }
      }

      let data: any = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        data = null;
      }

      console.log("[LoginModal] Login response data:", {
        status: res.status,
        data,
      });

      if (!res.ok) {
        let errorMsg = data?.error || text || "LOGIN FAILED";
        const errorCode = data?.code;

        // Provide user-friendly error messages
        if (errorCode === "EMAIL_NOT_CONFIRMED") {
          setEmailNotConfirmed(true);
          errorMsg =
            "Your email hasn't been confirmed yet. Please check your email inbox and click the verification link we sent you. If you don't see it, check your spam folder.";
        } else if (res.status === 403) {
          setEmailNotConfirmed(true);
          errorMsg =
            "Your email hasn't been confirmed yet. Please check your email inbox for the verification link.";
        } else if (errorCode === "USER_NOT_FOUND") {
          errorMsg = "This email is not registered. Please sign up first.";
        } else if (errorCode === "INVALID_PASSWORD") {
          errorMsg = "Email or password is incorrect.";
        } else if (errorCode === "DB_ERROR") {
          errorMsg = "Database connection error. Please try again in a moment.";
        } else if (res.status === 500) {
          errorMsg =
            "Server error. If this persists, contact support. (Error code: " +
            errorCode +
            ")";
        }

        throw new Error(errorMsg);
      }

      // 🔐 Do NOT store auth data in localStorage
      // Session is now handled by the HttpOnly cookie automatically.
      // Dispatch event for UI updates (kept for backward compatibility with other components)
      window.dispatchEvent(new Event("userUpdated"));

      toast({
        title: "WELCOME BACK",
        description: `SIGNED IN AS ${user.email}`,
      });

      setEmail("");
      setPassword("");
      closeLoginModal();

      // Navigate to assets after successful login
      setTimeout(() => {
        navigate("/assets");
      }, 500);
    } catch (err: any) {
      const errMsg = err?.message || "UNABLE TO LOGIN";
      console.error("[LoginModal] Login failed:", errMsg, err);
      toast({
        title: "LOGIN FAILED",
        description: errMsg,
      });
    } finally {
      setLoading(false);
    }
  }

  function resetAndClose() {
    setEmail("");
    setPassword("");
    closeLoginModal();
  }

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>LOG IN</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground">EMAIL</label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              disabled={loading}
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">PASSWORD</label>
            <div className="relative">
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                autoComplete="current-password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-2 text-muted-foreground"
                disabled={loading}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? "SIGNING IN…" : "LOG IN"}
          </Button>

          {emailNotConfirmed && (
            <Button
              type="button"
              variant="outline"
              disabled={resendingVerification}
              onClick={handleResendVerification}
              className="w-full"
            >
              {resendingVerification ? "SENDING…" : "RESEND VERIFICATION EMAIL"}
            </Button>
          )}

          <div className="text-xs text-muted-foreground text-center space-y-2">
            <div>
              Don't have an account?{" "}
              <a href="/auth" className="underline hover:no-underline">
                Sign up here
              </a>
            </div>
            <div>
              Forgot password?{" "}
              <a href="/auth" className="underline hover:no-underline">
                Reset here
              </a>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
